import { useEffect, useRef, useState } from "react";
import type { CoverBlock as CoverBlockType } from "@/types";
import { useRenderer } from "../context";
import { contactName, formatDate } from "@/lib/format";
import { computeTotals, formatMoney } from "@/lib/money";

/** Hero block (CD "cover" variant). A full-bleed dark band (set via the block
 *  background modifier): the reversed wordmark pins top-left, the content drops
 *  to the bottom — eyebrow, an ~88px Lato title (the project's site address),
 *  then a metadata row (Prepared for / Valid until / live Current total).
 *
 *  The band is sized to ~the whole display, which reads as a finished page to
 *  some readers — they never scroll and never see the quote itself. When there
 *  is anything below it (`showScrollCue`), the hero ends in a scroll cue. */
export function CoverBlock({
  block,
  showScrollCue,
}: {
  block: CoverBlockType;
  showScrollCue?: boolean;
}) {
  const { mode, quote, lineItems, selectedOptionalIds } = useRenderer();
  const rootRef = useRef<HTMLElement>(null);

  // The cue retires once they're moving — it has made its point, and on a
  // display too short for the hero (a phone held sideways) it is sitting over
  // the title until it goes. Public page only: the builder's preview scrolls
  // its own pane, not the window, so there the cue simply stays with the band.
  const [cueVisible, setCueVisible] = useState(true);
  useEffect(() => {
    if (!showScrollCue || mode !== "public") return;
    const onScroll = () => setCueVisible(window.scrollY < 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [showScrollCue, mode]);

  /** Scroll past the hero to the first content band. `scrollIntoView` (rather
   *  than `window.scrollTo`) so it also works inside the builder's scrollable
   *  preview pane; smooth unless the reader asks for less motion. */
  function scrollToDetail() {
    const next = rootRef.current?.closest(".block-bg")?.nextElementSibling;
    if (!next) return;
    const reduce =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    next.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  }

  // Omit "Valid until" when the expiry is 0/unset or unparseable.
  const expiry = quote?.expiry_date;
  const validUntilRaw = expiry ? formatDate(expiry) : "—";
  const validUntil = validUntilRaw !== "—" ? validUntilRaw : null;

  const currency = quote?.currency ?? "GBP";
  const vatTreatment = quote?.vat_treatment || "inclusive";
  const total = computeTotals(lineItems, vatTreatment, selectedOptionalIds).total;
  const hasItems = lineItems.length > 0;

  // CD hero: the big H1 is the project's site address (first line); fall back to
  // the block headline, then a generic.
  const siteLine = quote?.site_first_line_of_address || quote?.site_address_line_1;
  const title = siteLine || block.props.title || "Your project";

  return (
    <header className="block block--cover" ref={rootRef}>
      <img className="block--cover__mark" src="/cd/wordmark-reverse.png" alt="Arkitectly" />
      <div className="block--cover__body">
        <p className="eyebrow">Project proposal</p>
        <h1 className="block--cover__title">{title}</h1>
        {block.props.subtitle ? (
          <p className="block--cover__subtitle">{block.props.subtitle}</p>
        ) : null}
        {quote ? (
          <dl className="block--cover__meta">
            <div>
              <dt>Prepared for</dt>
              <dd>{contactName(quote)}</dd>
            </div>
            {validUntil ? (
              <div>
                <dt>Valid until</dt>
                <dd className="num numeric">{validUntil}</dd>
              </div>
            ) : null}
            {hasItems ? (
              <div>
                <dt>Current total</dt>
                <dd className="num numeric">{formatMoney(total, currency)}</dd>
              </div>
            ) : null}
          </dl>
        ) : null}
      </div>
      {showScrollCue ? (
        <button
          type="button"
          className="block--cover__scroll"
          data-hidden={cueVisible ? undefined : "true"}
          onClick={scrollToDetail}
        >
          Scroll for the full quote
          <span className="block--cover__chev" aria-hidden="true" />
        </button>
      ) : null}
    </header>
  );
}
